import { useState } from "react";
import { auth } from "../firebase";
import { API_BASE_URL } from "../config";
import { approveCortevaInvoice } from "../utils/cortevaInvoiceRegistry";
import { runWithCorrectionPrompt } from "../services/financeCore/invoiceCorrection";

const pkr = (amount, decimals = 0) => `PKR ${Number(amount || 0).toLocaleString("en-PK", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

export default function CortevaCropConsultant() {
  const [consultancyFee, setConsultancyFee] = useState(100000);
  const [serviceAdminCharge, setServiceAdminCharge] = useState(1250);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [period, setPeriod] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [approved, setApproved] = useState(false);

  const consultancyTax = Math.round(Number(consultancyFee || 0) * 0.04);
  const subtotal = Number(consultancyFee || 0) + consultancyTax;
  const gstOnAdmin = Math.round(Number(serviceAdminCharge || 0) * 0.16);
  const total = subtotal + Number(serviceAdminCharge || 0) + gstOnAdmin;

  async function download() {
    if (!invoiceNumber.trim() || !invoiceDate || !period.trim() || !(Number(consultancyFee) > 0)) {
      setMessage("Enter invoice number, date, payroll month, and a consultancy fee greater than zero.");
      return false;
    }
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setMessage("Sign in before generating a PDF.");
      return false;
    }
    const token = await currentUser.getIdToken();
    const response = await fetch(`${API_BASE_URL}/invoices/download`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        invoiceType: "corteva-crop-consultant",
        invoiceNumber: invoiceNumber.trim(), invoiceDate, period: period.trim(),
        consultancyFee: Number(consultancyFee), serviceAdminCharge: Number(serviceAdminCharge)
      })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `PDF generation failed (${response.status}).`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `CortevaCrop-Consultant-${invoiceNumber.trim().replace(/[^a-zA-Z0-9_-]/g, "-")}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  }

  async function generate() {
    setBusy(true);
    setMessage("");
    try {
      const ok = await download();
      if (ok) setMessage("PDF downloaded. Review it before sending to the client.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    setBusy(true);
    setMessage("");
    try {
      const saved = await runWithCorrectionPrompt((options) => approveCortevaInvoice({
        invoiceNumber, invoiceDate, period,
        segmentKey: "consultantCrop", segmentName: "Consultant (Corteva Crop)",
        headCount: 0, reimbursement: 0, reimbursementBreakdown: null,
        serviceCharges: Number(consultancyFee) + Number(serviceAdminCharge),
        invoiceAmount: Number(consultancyFee) + Number(serviceAdminCharge),
        gstAmount: consultancyTax + gstOnAdmin,
        grossAmount: total,
        consultancyFee: Number(consultancyFee), consultancyTax,
        serviceAdminCharge: Number(serviceAdminCharge), gstOnAdmin
      }, options));
      setMessage(saved.unchanged
        ? "Already approved with exactly these figures — nothing to change."
        : saved.corrected ? "Invoice corrected for Corteva Crop." : "Approved and recorded for Corteva Crop.");
      setApproved(true);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return <>
    <section className="panel"><div className="panel-header"><div><h2 className="panel-title">Corteva Crop — Consultant (fixed monthly fee)</h2><p className="page-copy">No employee upload — a flat monthly retainer. Figures default from the agreed amounts but are editable per invoice.</p></div></div><div className="panel-body">
      <div className="form-grid">
        <div className="field"><label>Consultancy fee</label><input className="input" type="number" min="0" step="0.01" value={consultancyFee} onChange={(event) => setConsultancyFee(event.target.value)} /></div>
        <div className="field"><label>Service administration charges</label><input className="input" type="number" min="0" step="0.01" value={serviceAdminCharge} onChange={(event) => setServiceAdminCharge(event.target.value)} /></div>
      </div>
      <div className="summary-grid" style={{ marginTop: 18 }}>
        <div className="stat-card"><span>Consultancy tax @4%</span><strong>{pkr(consultancyTax)}</strong></div>
        <div className="stat-card"><span>Subtotal incl. consultancy tax</span><strong>{pkr(subtotal)}</strong></div>
        <div className="stat-card"><span>GST on admin charges @16%</span><strong>{pkr(gstOnAdmin)}</strong></div>
        <div className="stat-card"><span>Total monthly charges</span><strong>{pkr(total)}</strong></div>
      </div>
    </div></section>
    <section className="panel" style={{ marginTop: 24 }}><div className="panel-header"><div><h2 className="panel-title">Invoice details</h2><p className="page-copy">Generating a PDF does not reserve or approve the invoice number.</p></div></div><div className="panel-body">
      <div className="form-grid">
        <div className="field"><label>Invoice number *</label><input className="input" value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} placeholder="e.g. 27-001/CorCrop/ALM" /></div>
        <div className="field"><label>Invoice date *</label><input className="input" type="date" value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} /></div>
        <div className="field"><label>Payroll month *</label><input className="input" value={period} onChange={(event) => setPeriod(event.target.value)} placeholder="Sep-26" /></div>
      </div>
      <button className="button button-primary" type="button" disabled={busy} onClick={generate}>{busy ? "Working…" : "Generate PDF"}</button>
      <button className="btn btn-success" type="button" style={{ marginTop: 8, marginLeft: 8 }} disabled={busy || approved} onClick={approve}>{approved ? "Approved" : "Approve & Save Invoice"}</button>
      {message && <p className="page-copy" role="status" style={{ marginTop: 12 }}>{message}</p>}
    </div></section>
  </>;
}
